import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getGatewayGetTransactionsExportDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-export.form-default-values';
import { GatewayGetTransactionsExportModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-export.model';
import { endOfToday, formatISO, startOfToday, subDays } from 'date-fns';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';

import { useLazyCpmsBoGetTransactionsCsvQuery } from '../../../core/service/cpms-bo-transaction.service';
import { downloadExport } from '../helper/export.helper';
import { ExportTransactionListContextProvider } from '../transaction.context';
import { TransactionListExportFormController } from './transaction-list-export-form/transaction-list-export-form.controller';

interface TransactionListExportControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
  chargePointId?: GatewayGetTransactionsExportModel['chargePointId'];
}

export const TransactionListExportController = ({
  onCancel,
  onSuccess,
  chargePointId,
}: TransactionListExportControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [lazyGetTransactionsCsvQuery] = useLazyCpmsBoGetTransactionsCsvQuery();

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<GatewayGetTransactionsExportModel> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(
      t('$.transaction-list-export.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    GatewayGetTransactionsExportModel
  > = async (fieldValues) => {
    const transactionListExport = {
      colmuns: fieldValues.columns,
      language: fieldValues.language,
      createdAfter: fieldValues.createdAfter
        ? formatISO(fieldValues.createdAfter)
        : undefined,
      createdBefore: fieldValues.createdBefore
        ? formatISO(fieldValues.createdBefore)
        : undefined,
      chargePointId,
    };

    try {
      const { isError, isSuccess, data, error } =
        await lazyGetTransactionsCsvQuery(transactionListExport);

      if (isError) {
        throw new Error(JSON.stringify(error));
      }

      if (isSuccess) {
        await downloadExport(transactionListExport, data);
        toast.success(t('$.transaction-list-export.success'));
        onSuccess();
      }
    } catch (error: any) {
      toast.error(
        t('$.transaction-list-export.error', {
          error: error.toString(),
        }),
      );
    }
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getGatewayGetTransactionsExportDefaultValues(
    subDays(startOfToday(), 7),
    endOfToday(),
  );

  return (
    <ExportTransactionListContextProvider data={data}>
      <TransactionListExportFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
      />
    </ExportTransactionListContextProvider>
  );
};
